import type HotelPackage from '@itinerary/hotel/HotelPackage';
import HotelPriceClass from '@itinerary/hotel/HotelPriceClass';
import type HotelRoomType from '@itinerary/hotel/HotelRoomType';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import type PriceClass from '@itinerary/PriceClass';
import { type PriceClassExtraOptionList } from '@itinerary/PriceClassExtraOption';
import type PriceClassExtraOption from '@itinerary/PriceClassExtraOption';
import type PriceType from '@itinerary/PriceType';
import type Flight from '@itinerary/trip/flight/Flight';
import type FlightPriceClass from '@itinerary/trip/flight/FlightPriceClass';
import { type SelectedSuggestionServices } from '@itinerary/trip/SuggestionService';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import { ReserveType } from '@utils/itinerary';
import { SellTypeCode } from '@utils/train';
import type FlightPackage from '@itinerary/trip/flight/FlightPackage';

export class PackageSelectStore {
    //#region state
    selectedPackages: ItineraryPackage[] = [];
    selectedServicesSuggestedFieldsError: { id: number; error: string }[] = [];
    //#endregion

    /**
     * Currently selected package, that is waiting to be accepted in raise class page
     *
     * @readonly
     * @type {(ItineraryPackage | undefined)}
     */
    get currentlySelectedPackage() {
        return this.selectedPackages[useItineraryStore().currentSetIndex];
    }

    get currentPackageIsFloating() {
        return !!(
            this.currentlySelectedPackage?.isHotelPackage() &&
            this.currentlySelectedPackage.floatingMode
        );
    }

    get hasCarSelect() {
        const currentPackage = this.currentlySelectedPackage;
        return !!(
            currentPackage?.isTrainPackage() &&
            currentPackage?.defaultSellType?.code === SellTypeCode.Car
        );
    }

    get billMessages() {
        return this.selectedPackages.flatMap(pkg => pkg.billMessage);
    }

    get gatewayMessages() {
        return this.selectedPackages.reduce((prev, next) => {
            const message = next.gateWayMessage;
            if (message) {
                return prev.includes(message) ? prev : [...prev, message];
            }
            return prev;
        }, [] as string[]);
    }

    get eventMetric() {
        const authStore = useAuthStore();
        return {
            providerName: (this.currentlySelectedPackage as FlightPackage)
                ?.providerName,
            hotelName: (this.currentlySelectedPackage as HotelPackage)
                ?.hotelName,
            departureTime: (this.currentlySelectedPackage as TripPackage)
                ?.departureTime,
            arrivalTime: (this.currentlySelectedPackage as TripPackage)
                ?.arrivalTime,
            vpn: authStore.vpn,
        };
    }

    suggestedServiceFormValidation() {
        // this function will work for all selected suggested services and can support more logic in the future if it ever needed
        const errors: { id: number; error: string }[] = [];
        this.selectedPackages.forEach(currentPackage => {
            (currentPackage as TripPackage).selectedSuggestionServices.forEach(
                ({ service, description }) => {
                    if (service.needDescription) {
                        if ((description || '').trim().length < 10) {
                            errors.push({
                                id: service.id,
                                error: 'حداقل ۱۰ کاراکتر وارد کنید.',
                            });
                        }
                    }
                }
            );
        });
        this.selectedServicesSuggestedFieldsError = errors;
    }

    clearSelectedPackages() {
        this.selectedPackages = [];
    }

    loadPreInvoiceForCurrentPackages() {
        this.selectedPackages.forEach(async pkg => {
            await useInvoiceStore().loadPreInvoice(pkg);
        });
    }

    /**
     * Modifies the selected price type, for the currently selected package
     *
     * @returns
     * @memberof PackageSelectStore
     * @param params
     */
    selectPriceTypeForPackageIndex(params: {
        priceType: PriceType<TripPriceClass>;
        index: number;
    }) {
        const itineraryPackage = this.selectedPackages[params.index];
        if (itineraryPackage.isTripPackage()) {
            if (
                itineraryPackage.selectedPrice &&
                !params.priceType.classes.includes(
                    itineraryPackage.selectedPrice
                )
            ) {
                itineraryPackage.selectedPrice =
                    params.priceType.classes.find(c => c.capacity > 0) || null;
            }
        } else {
            throw new Error('type of itinerary package not considered');
        }
    }

    /**
     * Modifies the selected price class, for the selected package
     *
     * @param {{
     *      priceClass: PriceClass;
     *      count: number;
     *  }} data
     * @returns
     * @memberof PackageSelectStore
     */
    selectPriceClassForPackageIndex(data: {
        priceClass: PriceClass;
        count: number;
        packageIndex: number;
    }) {
        const itineraryPackage = this.selectedPackages[data.packageIndex];
        if (itineraryPackage.isTripPackage()) {
            itineraryPackage.selectedPrice = data.priceClass as TripPriceClass;
            itineraryPackage.allGroupedTrips.forEach(t => {
                if ((t as Flight).cabinClass !== undefined) {
                    (t as Flight).cabinClass = (
                        data.priceClass as FlightPriceClass
                    ).cabinClass;
                }
            });
        } else if (itineraryPackage.isHotelPackage()) {
            const hotelPriceClass = data.priceClass as HotelPriceClass;
            if (!itineraryPackage.selectedPrices) {
                itineraryPackage.selectedPrices = [];
            }
            if (
                itineraryPackage.selectedPrices.some(
                    hP => hP.id === hotelPriceClass.id
                )
            ) {
                itineraryPackage.selectedPrices =
                    itineraryPackage.selectedPrices.filter(
                        priceClass => priceClass.id !== hotelPriceClass.id
                    );
            }
            for (let i = 0; i < data.count; i++) {
                itineraryPackage.selectedPrices.push(
                    new HotelPriceClass(hotelPriceClass.room, {
                        extraBedCapacity: hotelPriceClass.extraBedCapacity,
                        hasExtraBed: hotelPriceClass.hasExtraBed,
                        checkoutHour: itineraryPackage.checkoutHour,
                        checkinHour: itineraryPackage.checkinHour,
                        uniqueId: hotelPriceClass.uniqueId,
                        onlyThisClassSelectable:
                            hotelPriceClass.onlyThisClassSelectable,
                    })
                );
            }
        } else {
            throw new Error('type of itinerary package not considered');
        }
    }

    /**
     * clear the selected price class, for the selected package
     *
     * @param {{
     *  priceClass: PriceClass;
     *      count: number;
     *  }} data
     * @returns
     * @memberof PackageSelectStore
     */
    clearSelectPriceClassForPackageIndex(data: { packageIndex: number }) {
        const itineraryPackage = this.selectedPackages[data.packageIndex];
        itineraryPackage.selectedPrices = [];
    }

    /**
     * Modifies the selected price class, for the selected package
     *
     * @param {{
     *      option: PriceClassExtraOption;
     *      enable: boolean;
     *  }} params
     * @returns
     * @memberof PackageSelectStore
     */
    toggleExtraOptionsForSelectedPrice(params: {
        option: PriceClassExtraOption;
        priceClassIndex: number;
        enable: boolean;
        type?: 'form' | 'raise';
        package: ItineraryPackage;
    }) {
        const type = params.type || 'raise';
        const itineraryPackage = params.package;
        if (!itineraryPackage?.selectedPrices?.length) {
            return;
        }
        switch (type) {
            case 'raise': {
                // Removing option
                const options = itineraryPackage.selectedPrices[
                    params.priceClassIndex
                ].selectedExtraOptions.filter(o => o.id !== params.option.id);

                // Reading the option, if enabled
                itineraryPackage.selectedPrices[
                    params.priceClassIndex
                ].selectedExtraOptions = [
                    ...options,
                    ...(params.enable ? [params.option] : []),
                ];
                break;
            }
            case 'form': {
                // Removing option
                const options = itineraryPackage.selectedPrices[
                    params.priceClassIndex
                ].selectedExtraFormOptions.filter(
                    o => o.id !== params.option.id
                );

                // Reading the option, if enabled
                itineraryPackage.selectedPrices[
                    params.priceClassIndex
                ].selectedExtraFormOptions = [
                    ...options,
                    ...(params.enable ? [params.option] : []),
                ];
                break;
            }
            default:
                break;
        }
    }

    resetSelectedExtraFormOptions() {
        this.selectedPackages.forEach(pkg => {
            if (
                pkg.selectedPrices?.some(
                    price => price.selectedExtraFormOptions.length
                )
            ) {
                pkg.selectedPrices.forEach(price => {
                    price.selectedExtraFormOptions = [];
                });
            }
        });
    }

    getExtraOptionValueForSelectedPrice(params: {
        option: PriceClassExtraOption;
        priceClassIndex: number;
        package: ItineraryPackage;
    }) {
        const itineraryPackage = params.package;
        if (!itineraryPackage?.selectedPrices?.length) {
            return false;
        }

        // Removing option
        return !!itineraryPackage.selectedPrices[
            params.priceClassIndex
        ].selectedExtraOptions.find(o => o.id === params.option.id);
    }

    /**
     * change extra person for hotel
     *
     * @param {{ count: number; price: HotelPriceClass }} params
     * @memberof PackageSelectStore
     */
    changeExtraPerson(params: { count: number; price: HotelPriceClass }) {
        // temp add age 20 maybe need to get age
        const priceClasses =
            (this.selectedPackages[0] as HotelPackage).selectedPrices?.filter(
                p => p.id === params.price.id
            ) || [];
        let remainCount = params.count;
        priceClasses.forEach(price => {
            if (remainCount > 0) {
                if (params.price.extraBedCapacity >= remainCount) {
                    price.selectedExtraPerson = Array(remainCount).fill(20);
                    remainCount = 0;
                } else {
                    price.selectedExtraPerson = Array(
                        params.price.extraBedCapacity
                    ).fill(20);
                    remainCount -= params.price.extraBedCapacity;
                }
            } else {
                price.selectedExtraPerson = [];
            }
        });
    }

    getExtraPersonCounts(price: HotelPriceClass): number {
        const priceClasses =
            (this.selectedPackages[0] as HotelPackage).selectedPrices?.filter(
                p => p.id === price.id
            ) || [];

        return priceClasses.reduce(
            (count, current) => count + current.selectedExtraPerson.length,
            0
        );
    }

    get hasRaisePage() {
        const { getPassCount } = useSearchFormStore();
        return this.selectedPackages.some(pkg =>
            pkg.checkNeedsRaiseClass(getPassCount(pkg))
        );
    }

    /**
     * change request from hotel
     *
     * @param {string} request
     * @returns
     * @memberof PackageSelectStore
     */
    changeRequestFromHotel(request: string) {
        const [itineraryPackage] = this.selectedPackages;
        if (
            itineraryPackage &&
            !Object.prototype.hasOwnProperty.call(
                itineraryPackage,
                'requestFromHotel'
            )
        ) {
            return;
        }
        (itineraryPackage as HotelPackage).requestFromHotel = request;
    }

    setSelectedPackage(params: { package: ItineraryPackage; index: number }) {
        this.selectedPackages[params.index] = params.package;
    }

    removeSelectedPackage(index: number) {
        this.selectedPackages.splice(index);
    }

    setCurrentHotelPackagePrices(rooms: HotelRoomType[]) {
        (this.selectedPackages[0] as HotelPackage).loadedPrices = rooms;
    }

    /**
     * get an itinerary package and return available price types(filters on capacity > 0)
     *
     * @param {ItineraryPackage} pkg
     * @return {*}  {PriceType<PriceClass>[]}
     * @memberof PackageSelectStore
     */
    getAvailablePriceTypes(pkg: ItineraryPackage): PriceType<PriceClass>[] {
        return pkg.prices?.filter(p => p.classes.some(c => c.capacity > 0));
    }

    /**
     * GETTER - Returns the selected price type of the package
     *
     * @memberof PackageSelectStore
     */
    getSelectedPriceType(
        pkg: ItineraryPackage
    ): PriceType<TripPriceClass> | null {
        return (
            (pkg as TripPackage | undefined)?.prices.find(
                t =>
                    (pkg as TripPackage | undefined)?.selectedPrice &&
                    t.classes.includes((pkg as TripPackage).selectedPrice!)
            ) || null
        );
    }

    /**
     * get available price classes of a price type (filter on capacity)
     *
     * @param {PriceType<PriceClass>} priceType
     * @return {*}  {PriceClass[]}
     * @memberof PackageSelectStore
     */
    getAvailablePriceClasses(priceType: PriceType<PriceClass>): PriceClass[] {
        return priceType.classes
            ?.filter((p: PriceClass) => p.capacity > 0)
            ?.filter((p: PriceClass) => p.isReservable);
    }

    /**
     * get available extra options of an itinerary package base on root module pax counts
     *
     * @param {ItineraryPackage} pkg
     * @return {*}  {PriceClassExtraOptionList[]}
     * @memberof PackageSelectStore
     */
    getAvailableExtraOptionsList(
        pkg: ItineraryPackage
    ): PriceClassExtraOptionList[] {
        const currentSearchForm = useCurrentSearchForm();
        return (
            pkg.selectedPricesWithPriceClass
                ?.map((priceT, index) => ({
                    title: priceT[1].title,
                    selectedPriceClassIndex: index,
                    list:
                        priceT[0].getAvailableExtraOptions({
                            adultCount: currentSearchForm.value.adultCount,
                            childCount: currentSearchForm.value.childCount,
                            infantCount: currentSearchForm.value.infantCount,
                        }) || [],
                }))
                .filter(ex => ex.list.length) || []
        );
    }

    /**
     * Selects a package and proceeds to the next step
     * @param params Contains package and index to be selected
     */
    async selectPackage(params: {
        package: ItineraryPackage;
        index: number;
        bypassRaise?: boolean;
    }) {
        useCurrentItineraryModule().value.beforeSelectPackage({
            package: params.package,
            index: params.index || 0,
        });
        this.setSelectedPackage(params);
        // Finding the next index
        let nextIndex = 0;
        while (this.selectedPackages[nextIndex]) {
            nextIndex++;
        }
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        // Check that all needed packages are selected, or not
        if (nextIndex >= itineraryStore.expectedPackagesToBeSelected) {
            // All selected, so we proceed to the reserve process
            const needRaise = this.hasRaisePage;
            if (
                searchFormStore.reserveType === ReserveType.Bus &&
                searchFormStore.firstReserveType
            ) {
                searchFormStore.setReserveType(
                    searchFormStore.firstReserveType
                );
            }
            if (needRaise && !params.bypassRaise) {
                useRouter().push('/reserve/raise');
            } else {
                useRouter().push('/reserve/form');
            }
            useInvoiceStore().clearState();
        } else {
            // We must continue the selection in the next index
            itineraryStore.currentSetIndex = nextIndex;
            useContentStore().getSearchResultContents();
            if (params.index === 0) {
                searchFormStore
                    .currentSearchForm()
                    .priceLoader?.setCurrentSelectionStep('return');
            }
            await itineraryStore.loadAvailable({
                index: nextIndex,
            });
        }
    }

    /**
     * Deselects a package and proceeds to the prev step
     * @param index
     */
    deselectPackage(index: number) {
        this.removeSelectedPackage(index);
        // Finding the next index
        let nextIndex = 0;
        while (this.selectedPackages[nextIndex]) {
            nextIndex++;
        }
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        itineraryStore.currentSetIndex = nextIndex;
        itineraryStore.loadAvailable({
            index: nextIndex,
        });
        useContentStore().getSearchResultContents();
        if (index === 0) {
            searchFormStore
                .currentSearchForm()
                .priceLoader?.setCurrentSelectionStep('departure');
        }
    }

    /**
     * add selected a suggestion to current package
     */
    selectSuggestionService({
        service,
        count,
        description,
        packageIndex,
    }: SelectedSuggestionServices & { packageIndex: number }) {
        const itineraryPackage = this.selectedPackages[packageIndex];
        if (itineraryPackage.isTripPackage()) {
            const selectedIndex =
                itineraryPackage.selectedSuggestionServices.findIndex(
                    s => s.service === service
                );
            if (selectedIndex > -1 && count > 0) {
                const current =
                    itineraryPackage.selectedSuggestionServices[selectedIndex];
                current.count = count;
                current.description = description;
            } else if (selectedIndex > -1 && count <= 0) {
                itineraryPackage.selectedSuggestionServices.splice(
                    selectedIndex,
                    1
                );
            } else if (count > 0) {
                itineraryPackage.selectedSuggestionServices.push({
                    count,
                    service,
                });
            }
        } else {
            throw new Error('type of itinerary package not considered');
        }
    }
}

export const {
    use: usePackageSelectStore,
    useIfInstalled: usePackageSelectStoreIfInstalled,
} = defineClassStore('packageSelect', () => new PackageSelectStore());
